import { exercise, number, section } from './helpers.ts';

export const math = [
  exercise({
    id: 'math-dice',
    category: 'math',
    title: 'Two dice',
    topic: 'Conditional probability',
    difficulty: 'Moderate',
    questions: [
      number(
        'dice',
        'Two distinguishable, fair six-sided dice are rolled independently. You are told that at least one die shows 6. Given exactly that information, what is the probability that their sum is at least 10?',
        5 / 11,
        'probability',
        'There are 11 equally likely ordered outcomes containing a 6. Five have sum at least 10: (6,4), (6,5), (6,6), (4,6), (5,6).',
        ['probability', 'conditioning', 'restrict', 'count', 'protocol'],
      ),
    ],
    reading: {
      title: 'Conditional probability',
      sources: [],
      sections: [
        section(
          'probability',
          'Outcomes, events, and probability',
          [
            {
              kind: 'paragraph',
              text: 'Probability describes the chance of an event on a scale from zero to one. An outcome is one complete result of an experiment; an event is a collection of results we care about. The notation P(A) means the probability of the event named A.',
            },
            {
              kind: 'paragraph',
              text: 'When all elementary outcomes are equally likely, count the favorable ones and divide by the total. With unequal chances, add the probabilities of the outcomes instead: counting three even faces of a biased die would not establish a chance of one half.',
            },
            {
              kind: 'equation',
              label: 'Counting equally likely outcomes',
              lines: [
                {
                  left: 'P(A)',
                  right: {
                    numerator: 'outcomes in A',
                    denominator: 'all outcomes',
                  },
                },
              ],
              note: 'Use counts only when the elementary outcomes are equally likely.',
            },
            {
              kind: 'example',
              title: 'Example · One fair die',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'The six outcomes are 1 through 6. The event “even” contains 2, 4, and 6. Three of the six equally likely outcomes are favorable.',
                },
                {
                  kind: 'equation',
                  label: 'Count the favorable share',
                  lines: [
                    {
                      left: 'P(even)',
                      right: {
                        numerator: '3',
                        denominator: '6',
                      },
                    },
                  ],
                  note: 'This reduces to one half.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: "With two random choices, a complete outcome must record both. If a fair coin and a fair die are independent, knowing either result does not change the other's probabilities. There are 2 × 6 = 12 equally likely pairs. Multiplication counts the pairs because each coin result can accompany all six die results. The event “heads and even” contains three pairs and has probability 3/12.",
            },
          ],
          'Concept',
        ),
        section(
          'conditioning',
          'What “given” changes',
          [
            {
              kind: 'paragraph',
              text: 'Conditioning means restricting attention to outcomes consistent with new information. The event after “given” becomes the population we compare against. Within that population, we ask what share also satisfies the event of interest.',
            },
            {
              kind: 'equation',
              label: 'Conditional probability',
              lines: [
                {
                  left: 'P(A | B)',
                  right: {
                    numerator: 'P(A and B)',
                    denominator: 'P(B)',
                  },
                },
              ],
              note: 'A is the event of interest; B is the information already known. P(B) must be positive. The denominator rescales the surviving population to a total probability of one.',
            },
            {
              kind: 'example',
              title: 'Example · A starred ticket',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'New information narrows the outcomes still possible. Suppose a bag contains ten equally likely tickets: four red and six blue. Two red tickets and one blue ticket have a star. Before looking, the chance of red is 4/10. If we learn the selected ticket has a star, we are now choosing among three starred tickets, two of them red. The chance of red given a star is 2/3. We did not change the ticket; we changed the group against which we compare it.',
                },
                {
                  kind: 'diagram',
                  drawing: 'conditional-grid',
                  caption: 'Conditioning changes the denominator.',
                  description:
                    'Of ten tickets, four are red and six blue. Two red tickets and one blue ticket have stars. Restricting to the three starred tickets leaves two red tickets, so the red share is two thirds.',
                },
                {
                  kind: 'equation',
                  label: 'Apply the definition',
                  lines: [
                    {
                      left: 'P(red | star)',
                      right: {
                        numerator: '2/10',
                        denominator: '3/10',
                      },
                    },
                  ],
                  note: 'The original total cancels: (2/10) ÷ (3/10) = 2/3.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'This also explains why P(A | B) is not usually P(B | A): two of three starred tickets are red, while two of four red tickets are starred. Before computing, name the group after “given.” Then find the part of that group meeting the event. In the dice problem below, this means identifying exactly what the observation tells us before counting large sums.',
            },
          ],
          'Method',
        ),
        section(
          'restrict',
          'Start with equally likely elementary outcomes',
          [
            {
              kind: 'paragraph',
              text: 'We are not being asked how often two unrestricted dice have a large sum. We already know that at least one die is 6. The task is to measure the proportion of outcomes with sum at least 10 among the outcomes consistent with that information.',
            },
            {
              kind: 'paragraph',
              text: 'Label the dice red and blue, even if the colors are only imaginary. An outcome is an ordered pair: the red value and the blue value. Independence and fairness make all thirty-six pairs equally likely. The pairs (4,6) and (6,4) are different physical outcomes, even though their sums agree.',
            },
            {
              kind: 'paragraph',
              text: 'Let B be the event that at least one die is 6, and A the event that the sum is at least 10. Once B is known, outcomes outside B are no longer candidates. Within B the surviving elementary outcomes remain equally likely. Conditional probability is P(A given B) = P(A and B) / P(B); counting works here because every elementary pair started with equal weight.',
            },
          ],
          'Daily application',
        ),
        section(
          'count',
          'Count the overlap only once',
          [
            {
              kind: 'paragraph',
              text: 'There are six pairs with red equal to 6 and six with blue equal to 6. The pair (6,6) appears in both lists, so B contains 6 + 6 − 1 = 11 outcomes. This subtraction is a small example of inclusion–exclusion: adding overlapping groups without correcting the overlap overcounts.',
            },
            {
              kind: 'paragraph',
              text: 'Among those eleven, the successful pairs are (6,4), (6,5), (6,6), (4,6), and (5,6). That gives 5/11. The pair (5,5), although it has a large enough sum, is excluded by the information we were given. The answer is therefore not the unconditional probability of a sum of at least 10, which would count six outcomes out of thirty-six.',
            },
            {
              kind: 'equation',
              label: 'Daily result',
              lines: [
                {
                  left: 'P(sum ≥ 10 | at least one 6)',
                  right: {
                    numerator: '5',
                    denominator: '11',
                  },
                },
              ],
              note: 'Count only the outcomes that remain possible after the observation.',
            },
            {
              kind: 'paragraph',
              text: 'A common wrong route treats “the die that is 6” as fixed and lets the other die range uniformly from 1 to 6. That model has six candidates and gives 3/6. But the information did not identify a particular die as the one showing 6, and (6,6) does not get two copies in the conditional sample space.',
            },
          ],
          'Daily application',
        ),
        section(
          'protocol',
          'Read the observation rule carefully',
          [
            {
              kind: 'paragraph',
              text: 'The way information is obtained can change the probability question. “The red die is 6” really would leave six equally likely possibilities for the blue die. “At least one is 6” leaves eleven ordered possibilities. And a person who chooses which result to report according to an unstated rule could create a different selection process.',
            },
            {
              kind: 'paragraph',
              text: 'For this problem, B means exactly “at least one die is 6.” A direct check is to list (6,1) through (6,6), then (1,6) through (5,6). That list has eleven entries, with no duplicate (6,6). Five entries meet the sum condition. It reproduces 5/11 without relying on a verbal choice of “the other die.”',
            },
          ],
          'Check',
        ),
      ],
    },
  }),
  exercise({
    id: 'math-coins',
    category: 'math',
    title: 'Waiting for a pattern',
    topic: 'Expected values',
    difficulty: 'Challenging',
    questions: [
      number(
        'coins',
        'A fair coin is tossed independently until two consecutive heads appear for the first time. Starting before any toss, what is the expected total number of tosses, including the final two heads?',
        6,
        'tosses',
        'Let E₀ be the expected remaining tosses with no trailing head and E₁ with one trailing head. E₀ = 1 + (E₀ + E₁)/2 and E₁ = 1 + E₀/2; solving gives E₀ = 6.',
        ['expectation', 'recurrence', 'states', 'equations', 'check'],
      ),
    ],
    reading: {
      title: 'Expected values and waiting times',
      sources: [],
      sections: [
        section(
          'expectation',
          'An expectation is a weighted average',
          [
            {
              kind: 'paragraph',
              text: 'A random variable assigns a number to each possible result. If we record the number of tosses until an event occurs, the variable is a count rather than a coin face. Its expected value is the average across many independent repetitions, weighted by how frequently each value occurs. It need not be a value that any single run can take, and it does not promise when a particular run will finish.',
            },
            {
              kind: 'equation',
              label: 'Expected value',
              lines: [
                {
                  left: 'E[X]',
                  right: 'Σ x · P(X = x)',
                },
              ],
              note: 'X is the numerical result. Σ means add over its possible values x. Each value is weighted by its probability.',
            },
            {
              kind: 'example',
              title: 'Example · A task with two possible durations',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'For a finite example, suppose a task takes one minute with probability 3/4 and five minutes with probability 1/4. Its expected duration is (3/4) × 1 + (1/4) × 5 = 2 minutes. Averaging 1 and 5 to get 3 would incorrectly give the rare case equal weight. In general, E[X] = sum of x × P(X = x) over all possible values x. E[X] means the expected value of the random variable X; the sum adds each possible value multiplied by its probability.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Waiting times can have infinitely many possible values, since a long string of failures may occur. We could add infinitely many weighted terms when the sum converges, but a simpler method often comes from grouping runs by what happens next. If the next step has two outcomes, the overall future average is the probability-weighted average of the future waits in those two groups. This is the law of total expectation in a two-case form.',
            },
          ],
          'Concept',
        ),
        section(
          'recurrence',
          'Build an equation from a restart',
          [
            {
              kind: 'paragraph',
              text: 'First consider waiting for just one head from a fair coin. Let E be the expected number of remaining tosses before any toss is made. We must spend one toss. With probability 1/2 it is a head and the remaining wait is zero. With probability 1/2 it is a tail, and independence puts us back in exactly the original situation, with expected remaining wait E. Therefore E = 1 + (1/2) × 0 + (1/2) × E. Subtract E/2 from both sides: E/2 = 1, so E = 2.',
            },
            {
              kind: 'equation',
              label: 'One head: solve the restart equation',
              lines: [
                {
                  left: 'E',
                  right: '1 + ½ · 0 + ½ · E',
                  reason: 'One toss is certain; only a tail leaves a further wait.',
                },
                {
                  left: 'E − ½E',
                  right: '1',
                  reason: 'Subtract the restart term from both sides.',
                },
                {
                  left: 'E',
                  right: '2',
                  reason: 'Divide both sides by one half; the unit is tosses.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'An unknown appearing on both sides is not circular guessing. We describe the same average in two ways: before taking a step, and as the cost of that step plus the weighted future cost. The leading 1 counts the toss we actually make. The other terms count only later tosses, so we neither omit nor double-count the successful toss.',
            },
            {
              kind: 'paragraph',
              text: 'For a pattern, a failed step may leave partial progress instead of a complete restart. We then need one expectation for each distinct situation that changes our future chances. Such a situation is called a state. Two histories can share a state if they allow exactly the same future behavior, even if their past lengths differ. The next sections construct those states for consecutive heads and use the same “one step plus weighted future” equation for each.',
            },
          ],
          'Method',
        ),
        section(
          'states',
          'Remember only what affects the next step',
          [
            {
              kind: 'paragraph',
              text: 'We toss a fair coin until the first occurrence of HH and count every toss, including both heads in that final pair. An expectation is an average over repeated runs of this experiment; it is not a maximum waiting time. Some runs end after two tosses and others take much longer.',
            },
            {
              kind: 'paragraph',
              text: 'The complete history might be very long, but only its ending matters. If the last toss was a tail, or if no toss has happened, we have no partial run of heads. Call that state 0. If the last toss was a single head and we have not yet finished, call that state 1. A head from state 1 finishes the experiment.',
            },
            {
              kind: 'diagram',
              drawing: 'coin-states',
              caption: 'A state records the progress that affects the next toss.',
              description:
                'From no trailing head, a tail keeps the same state and a head creates one trailing head. From one trailing head, a tail resets and a head finishes. Every transition costs one toss and has probability one half.',
            },
            {
              kind: 'paragraph',
              text: 'Let E₀ and E₁ be the expected numbers of additional tosses needed in these two states. These are remaining waits, not counts of tosses already made. Defining that distinction clearly prevents an off-by-one error at the stopping point.',
            },
          ],
          'Daily application',
        ),
        section(
          'equations',
          'Condition on the next toss',
          [
            {
              kind: 'paragraph',
              text: 'Use the state diagram to account for one toss and then the remaining wait. From state 0, the two outcomes leave states 0 and 1. From state 1, a head finishes and a tail resets. Because the coin is fair, both branches receive weight one half.',
            },
            {
              kind: 'equation',
              label: 'Daily application: one equation per state',
              lines: [
                {
                  left: 'E₀',
                  right: '1 + ½E₀ + ½E₁',
                  reason: 'From no trailing head, average over a tail and a head.',
                },
                {
                  left: 'E₁',
                  right: '1 + ½E₀',
                  reason: 'A head finishes; its remaining cost is zero.',
                },
                {
                  left: 'E₀',
                  right: '1 + ½E₀ + ½(1 + ½E₀)',
                  reason: 'Substitute the second equation into the first.',
                },
                {
                  left: '¼E₀',
                  right: '3/2',
                  reason: 'Collect the terms containing E₀.',
                },
                {
                  left: 'E₀',
                  right: '6',
                  reason: 'Multiply by four; this is the expected starting wait.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Substituting the starting expectation into the second equation gives E₁ = 4. Starting with one trailing head is better than starting from scratch, but a tail can still erase that progress.',
            },
            {
              kind: 'paragraph',
              text: 'The leading 1 in each equation is essential. It pays for the next toss regardless of the result, including the toss that completes the pair. The expectations on the right count only what happens after that toss.',
            },
          ],
          'Daily application',
        ),
        section(
          'check',
          'Why a one-in-four pattern does not mean four tosses',
          [
            {
              kind: 'paragraph',
              text: 'A particular pair of tosses is HH with probability one quarter. But consecutive two-toss windows overlap, and failed windows do not all leave the same amount of progress. HT leaves no trailing head, whereas TH leaves one. Treating each toss as an independent one-quarter chance of completion loses this state information.',
            },
            {
              kind: 'paragraph',
              text: 'For comparison, waiting for a single head takes two tosses on average. After seeing a head, another head would finish immediately, but a tail forces a complete restart. An expected remaining wait of four from state 1 is consistent with that costly possibility. Six overall is an expectation over arbitrarily long runs, not a guaranteed deadline and not necessarily the most common finishing time.',
            },
            {
              kind: 'paragraph',
              text: 'We can also interpret E₁ = 4 directly. Starting with one trailing head, the next toss has a one-half chance of ending the run in one more toss. With the other one-half chance, it is a tail: we spend that toss and then face an average wait of six from state 0. The average is (1 + 7)/2 = 4. Having one head is useful, but it does not guarantee that the very next toss finishes the pair.',
            },
          ],
          'Check',
        ),
      ],
    },
  }),
  exercise({
    id: 'math-arrivals',
    category: 'math',
    title: 'Meeting at a café',
    topic: 'Geometric probability',
    difficulty: 'Moderate',
    questions: [
      number(
        'arrivals',
        'Two friends independently choose arrival times uniformly between noon and 1 p.m. Each stays for exactly 20 minutes after arriving, even if that extends beyond 1 p.m. What is the probability that their visits overlap?',
        5 / 9,
        'probability',
        'Plot the two arrivals in a 60-by-60 square. Non-overlap occupies two right triangles with legs 40, totaling area 1,600. The overlap probability is 1 − 1,600/3,600 = 5/9.',
        ['continuous', 'area', 'square', 'geometry', 'boundary'],
      ),
    ],
    reading: {
      title: 'Continuous and geometric probability',
      sources: [],
      sections: [
        section(
          'continuous',
          'Probability for a continuous choice',
          [
            {
              kind: 'paragraph',
              text: 'A time chosen uniformly within an interval is not chosen from a finite list of whole minutes. It can lie anywhere in the interval. Uniform means equal-length subintervals have equal probabilities. If a bus arrival is uniform across ten minutes, any particular two-minute interval inside that window has probability 2/10. The rule is favorable length divided by total length.',
            },
            {
              kind: 'equation',
              label: 'Uniform choice on an interval',
              lines: [
                {
                  left: 'P(arrival in an interval)',
                  right: {
                    numerator: 'interval length',
                    denominator: 'total window length',
                  },
                },
              ],
              note: 'The favorable interval must lie within the arrival window.',
            },
            {
              kind: 'paragraph',
              text: "An exact time has probability zero in this ideal continuous model, although an arrival certainly happens somewhere. To see why, surround that time by an interval of length one minute, then one second, then still less. The interval's probability shrinks in proportion to its length. A single point has no length. This is why an endpoint usually makes no difference to a continuous probability, unlike one specific face of a die.",
            },
            {
              kind: 'paragraph',
              text: "Probability density describes probability per unit length. A uniform ten-minute arrival has constant density 1/10 per minute: multiply that by an interval's length to get its probability. A density is not the probability of the point itself. With a nonuniform arrival, different intervals can have different weights, and a simple length ratio need not work.",
            },
          ],
          'Concept',
        ),
        section(
          'area',
          'Two independent choices make an area model',
          [
            {
              kind: 'example',
              title: 'Example · Two uniform coordinates',
              blocks: [
                {
                  kind: 'paragraph',
                  text: "For two independent uniform choices, draw one axis for each. As a separate example, choose X uniformly between 0 and 10 and Y uniformly between 0 and 20. A complete outcome is the point (X,Y) inside a 10-by-20 rectangle. The chance that X lies in a chosen two-unit interval is 2/10; the chance that Y lies in a chosen five-unit interval is 5/20. Independence lets us multiply: (2/10)(5/20) = 10/200. The numerator is the small rectangle's area; the denominator is the whole rectangle's area.",
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'This extends to a triangle or curved region by dividing it into small rectangles and refining the division. Under constant joint density, probability equals favorable area / total area. “Joint” means we are describing both choices together. If the choices influence each other, the points may concentrate along a curve or in particular regions, and area alone no longer gives the answer.',
            },
            {
              kind: 'equation',
              label: 'Independent uniform coordinates',
              lines: [
                {
                  left: 'P(region)',
                  right: {
                    numerator: 'area of region',
                    denominator: 'area of the whole rectangle',
                  },
                },
              ],
              note: 'Constant joint density is the reason area ratios work; arbitrary random coordinates need not have this property.',
            },
            {
              kind: 'paragraph',
              text: 'Translate the event into a geometric condition before measuring it. X < Y means points above the diagonal Y = X. An absolute difference |X − Y| means the nonnegative distance between the two numbers; |X − Y| ≤ w means they differ by at most w. Sometimes the unwanted region is easier to measure. An event and its complement—the outcomes where it does not occur—partition the whole space, so their probabilities add to one. Hence P(event) = 1 − P(complement). We will use this subtraction for overlapping visits.',
            },
          ],
          'Method',
        ),
        section(
          'square',
          'Give each random choice its own axis',
          [
            {
              kind: 'paragraph',
              text: 'Each friend can arrive at any time in the same one-hour window, and each then stays for twenty minutes. There are two independent random choices. To calculate the chance of meeting, we must count pairs of arrival times that put both friends in the café at once.',
            },
            {
              kind: 'paragraph',
              text: 'Let X and Y be minutes after noon for the two arrival times. Each is uniform on [0,60], and they are independent. Every pair (X,Y) lies in a square of side 60. Equal-area regions of this square have equal probabilities, because the joint density is constant at 1/3,600.',
            },
            {
              kind: 'paragraph',
              text: 'The visits overlap precisely when neither friend arrives more than twenty minutes after the other: |X − Y| ≤ 20. Whether we include equality does not change the answer in a continuous model; the boundary lines have area zero. The condition describes a diagonal band, not a twenty-minute interval on just one axis.',
            },
            {
              kind: 'diagram',
              drawing: 'arrival-area',
              caption: 'Meeting times form a diagonal band.',
              description:
                'The two arrival times span a 60-by-60 square. Overlap requires the times to differ by at most 20 minutes. Outside the diagonal band are two right triangles, each with legs of 40 minutes.',
            },
          ],
          'Daily application',
        ),
        section(
          'geometry',
          'Subtract the two easy regions',
          [
            {
              kind: 'paragraph',
              text: 'The band’s area can be found directly, but its complement is simpler. Above the line Y = X + 20 is the triangle where the second friend arrives too late. Its legs are 40, because the line intersects the square at (0,20) and (40,60). Its area is one half times 40 squared, or 800.',
            },
            {
              kind: 'paragraph',
              text: 'Below Y = X − 20 is an equal triangle where the first friend arrives too late. Together they have area 1,600. The favorable area is 3,600 − 1,600 = 2,000, giving probability 2,000/3,600 = 5/9.',
            },
            {
              kind: 'equation',
              label: 'Subtract the non-overlap area',
              lines: [
                {
                  left: 'Area of one triangle',
                  right: '½ × 40 × 40 = 800',
                  reason: 'Each triangle represents one friend arriving too late.',
                },
                {
                  left: 'P(no overlap)',
                  right: {
                    numerator: '2 × 800',
                    denominator: '60 × 60',
                  },
                  reason: 'Divide both triangles’ area by the full square’s area.',
                },
                {
                  left: 'P(overlap)',
                  right: '1 − 4/9 = 5/9',
                  reason: 'Overlap and non-overlap exhaust the possible pairs.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'More generally, if the arrival window is T minutes and the waiting time is w between zero and T, the probability is 1 − (T − w)²/T². At w = 0 the probability is zero; at w = T it is one. Those endpoint checks cannot prove the formula, but they help catch an incorrectly chosen triangle or denominator.',
            },
          ],
          'Daily application',
        ),
        section(
          'boundary',
          'Why forty out of sixty is too generous',
          [
            {
              kind: 'paragraph',
              text: 'If the first friend arrives at 12:30, arrivals of the second friend between 12:10 and 12:50 work: a forty-minute interval. But if the first arrives at noon, only the interval from noon to 12:20 is possible. The arrival window clips the useful interval near either edge. Applying the central forty-minute window to every first arrival would overestimate the answer as two thirds.',
            },
            {
              kind: 'paragraph',
              text: 'For another check, condition on the first arrival X. The second friend has X + 20 acceptable minutes when 0 ≤ X ≤ 20, forty acceptable minutes when 20 ≤ X ≤ 40, and 80 − X acceptable minutes when 40 ≤ X ≤ 60. The two edge intervals each have average length thirty; the middle has length forty. Averaging across three equal twenty-minute intervals gives (30 + 40 + 30)/3 = 100/3 acceptable minutes. Dividing by sixty again gives 5/9.',
            },
          ],
          'Check',
        ),
      ],
    },
  }),
  exercise({
    id: 'math-sampling',
    category: 'math',
    title: 'Sampling components',
    topic: 'Sampling without replacement',
    difficulty: 'Moderate',
    questions: [
      number(
        'sampling',
        'A batch contains 12 components, exactly 3 of which are defective. Four distinct components are selected uniformly at random, without replacement. What is the probability that exactly one selected component is defective?',
        28 / 55,
        'probability',
        'Choose 1 of the 3 defective components and 3 of the 9 good ones: C(3,1)C(9,3)/C(12,4) = 252/495 = 28/55.',
        ['combinations', 'composition', 'subsets', 'count', 'dependence'],
      ),
    ],
    reading: {
      title: 'Sampling without replacement',
      sources: [],
      sections: [
        section(
          'combinations',
          'Why combinations divide out order',
          [
            {
              kind: 'paragraph',
              text: 'Counting a selection starts with deciding what makes two results different. Choosing A and then B is different from choosing B and then A if we care about the order. If we only care which objects were selected, both describe the same subset {A,B}. Confusing those two questions is a common source of counting errors.',
            },
            {
              kind: 'example',
              title: 'Example · Choose two of four books',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Take four labeled books A, B, C, and D and choose two distinct books. An ordered choice has four options first and three second, giving 4 × 3 = 12 sequences. But each pair appears twice, as AB and BA for example. Dividing by two leaves six pairs: AB, AC, AD, BC, BD, CD. The denominator removes repeated descriptions of the same selection.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'For r distinct objects, the number of orders is r! (“r factorial”), meaning r × (r − 1) × … × 1. We define 0! = 1. Ordered selections of r objects from n have n × (n − 1) × … × (n − r + 1) possibilities. Dividing by r! gives C(n,r) = n! / [r!(n − r)!]. C(n,r), read “n choose r,” counts subsets, not probabilities. A probability arises when we divide the count of favorable, equally likely subsets by the count of all such subsets.',
            },
            {
              kind: 'equation',
              label: 'Combinations: remove repeated orders',
              lines: [
                {
                  left: 'C(n, r)',
                  right: {
                    numerator: 'n!',
                    denominator: 'r! (n − r)!',
                  },
                },
              ],
              note: 'n is the number available, r the number chosen, and r! counts the orders of the same selected objects.',
            },
          ],
          'Concept',
        ),
        section(
          'composition',
          'Count a sample with a required composition',
          [
            {
              kind: 'example',
              title: 'Example · One history book and two novels',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Suppose a shelf has two history books and four novels, and we select three distinct books uniformly. To get exactly one history book, choose one of the two history books and two of the four novels. There are C(2,1) = 2 choices for the first part and C(4,2) = 6 for the second. Every first choice can be paired with every second choice, giving 2 × 6 = 12 acceptable subsets. All three-book subsets number C(6,3) = 20. The probability is 12/20 = 3/5.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'In general, let N be the population size, K the number of special items, n the sample size, and r the number of special items wanted. The sample must contain r of the K special items and n − r of the N − K others. Its probability is C(K,r) × C(N − K,n − r) / C(N,n), called the hypergeometric probability. Each factor has a job: choose the special part, choose the ordinary part, then divide by all samples. Impossible requests, such as choosing more special items than exist, have probability zero.',
            },
            {
              kind: 'equation',
              label: 'Sampling a required composition',
              lines: [
                {
                  left: 'P(exactly r special items)',
                  right: {
                    numerator: 'C(K, r) · C(N − K, n − r)',
                    denominator: 'C(N, n)',
                  },
                },
              ],
              note: 'Choose the special part and the ordinary part, then divide by all samples. N = population; K = special items; n = sample size; r = desired special count.',
            },
            {
              kind: 'paragraph',
              text: 'This counts sampling without replacement: an item cannot be chosen twice. The multiplication in the numerator is a counting rule, not a claim that successive draws are independent. Indeed, removing one item changes the remaining population. A uniform subset can be produced by drawing distinct items one at a time uniformly from those remaining: every subset has the same number of possible orders and each complete order has the same probability. That is what justifies using equally likely subsets below.',
            },
          ],
          'Method',
        ),
        section(
          'subsets',
          'Choose an equally likely sample space',
          [
            {
              kind: 'paragraph',
              text: 'There are three defective and nine good components in the batch. We select four without replacing any. A successful sample contains exactly one defective component and three good ones. We need the probability of that composition, not the probability that a particular named component is selected.',
            },
            {
              kind: 'paragraph',
              text: 'The sample consists of four distinct components. We can ignore the order in which they were drawn because the event asks only how many are defective. Every four-component subset is equally likely under the stated sampling rule. There are C(12,4) such subsets, where C(n,r) means the number of ways to choose r objects from n without regard to order.',
            },
            {
              kind: 'paragraph',
              text: 'The formula is C(n,r) = n! / [r!(n − r)!]. One way to understand the denominator is to count ordered selections first, then divide by the r! orders that describe the same subset. Here the ordered count is 12 × 11 × 10 × 9. Each four-item subset appears in 4 × 3 × 2 × 1 = 24 orders. Dividing 11,880 by 24 gives C(12,4) = 495 equally likely subsets.',
            },
          ],
          'Daily application',
        ),
        section(
          'count',
          'Build each favorable sample exactly once',
          [
            {
              kind: 'paragraph',
              text: 'Exactly one defective means choosing one component from the three defective ones and three from the nine good ones. These choices can be combined freely, so the number of favorable samples is C(3,1) × C(9,3) = 3 × 84 = 252. Each favorable sample appears once: its defective member identifies the first choice, and its three good members identify the second.',
            },
            {
              kind: 'table',
              caption: 'Daily sample: count each part',
              headers: ['Selection', 'Count', 'Meaning'],
              rows: [
                ['One defective', 'C(3,1) = 3', 'Choose one of the three defective components.'],
                [
                  'Three good',
                  'C(9,3) = 84',
                  'Choose the remaining three from nine good components.',
                ],
                [
                  'Favorable samples',
                  '3 × 84 = 252',
                  'Pair every defective-subset choice with every good-subset choice.',
                ],
                ['All samples', 'C(12,4) = 495', 'Every four-component subset.'],
              ],
            },
            {
              kind: 'equation',
              label: 'Daily probability',
              lines: [
                {
                  left: 'P(exactly one defective)',
                  right: {
                    numerator: '252',
                    denominator: '495',
                  },
                },
              ],
              note: 'Reduce the fraction to 28/55. Counting subsets does not assume the successive draws are independent.',
            },
            {
              kind: 'paragraph',
              text: 'The required probability is therefore 252/495 = 28/55, a little over one half. Multiplication here counts combinations of choices; it does not assume that successive draws are independent. That distinction matters because the composition of the remaining batch changes with every draw.',
            },
            {
              kind: 'paragraph',
              text: 'The general pattern is called the hypergeometric distribution. If a population of N items contains K special items, a sample of size n has exactly r special items with probability C(K,r)C(N − K,n − r)/C(N,n). The formula is just the same two-part construction of a sample.',
            },
          ],
          'Daily application',
        ),
        section(
          'dependence',
          'See why the binomial shortcut changes the model',
          [
            {
              kind: 'paragraph',
              text: 'The fraction defective at the start is 3/12. After drawing a defective component, it is 2/11 among what remains; after drawing a good component, it is 3/11. Treating all four draws as independent with a fixed one-quarter defect probability describes sampling with replacement, or a different model in which each item’s defect status is independently generated.',
            },
            {
              kind: 'paragraph',
              text: 'An ordered check gives the same correct result. For the particular pattern defective–good–good–good, multiply (3/12)(9/11)(8/10)(7/9). There are four possible positions for the lone defective item, each with the same probability, so multiply by four to recover 28/55. This alternative calculation is useful as a verification, but the subset method avoids managing all the possible orders. Choose the representation that makes equally likely outcomes and the event easiest to see.',
            },
          ],
          'Check',
        ),
      ],
    },
  }),
  exercise({
    id: 'math-screening',
    category: 'math',
    title: 'Defective items',
    topic: 'Base rates and Bayes’ rule',
    difficulty: 'Challenging',
    questions: [
      number(
        'screening',
        'One percent of items on a production line are defective. An automated check flags 95% of defective items and also flags 5% of nondefective items. An item is chosen uniformly at random from all items the check flags. What is the probability it is defective?',
        19 / 118,
        'probability',
        'In 10,000 items, expected flags include 95 defective and 495 nondefective. The defective share of flagged items is 95/(95 + 495) = 19/118.',
        ['conditional', 'bayes', 'population', 'reverse', 'lesson'],
      ),
    ],
    reading: {
      title: 'Conditional probability and Bayes’ rule',
      sources: [],
      sections: [
        section(
          'conditional',
          'A conditional probability names its population',
          [
            {
              kind: 'paragraph',
              text: 'A probability is a share of possible outcomes. In a population where each item has the same chance of being selected, it is the relevant count divided by the population count. A conditional probability narrows the population using information already known. Write P(A | B) for “the probability of A given B”: among outcomes satisfying B, what share also satisfy A?',
            },
            {
              kind: 'example',
              title: 'Example · Borrowed library books',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'For example, among 100 library books, 40 are hardbacks, 30 are borrowed, and 12 are both. The probability a borrowed book is a hardback is 12/30. The probability a hardback is borrowed is 12/40. These are different questions, despite involving the same overlap of 12 books. The condition after the vertical bar determines the denominator.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Measured against the original 100 books, the overlap has probability 12/100 and the borrowed group 30/100. Dividing gives (12/100)/(30/100) = 12/30. This yields the general definition P(A | B) = P(A and B) / P(B), for P(B) > 0. Rearranging gives the multiplication rule P(A and B) = P(B | A)P(A). It says: first take the share in group A, then take the share of that group also in B. This conditional multiplication does not require independence.',
            },
            {
              kind: 'equation',
              label: 'The overlap, counted in either direction',
              lines: [
                {
                  left: 'P(A and B)',
                  right: 'P(B | A) · P(A)',
                },
              ],
              note: 'Start with the share in A, then take the share of that group also in B.',
            },
          ],
          'Concept',
        ),
        section(
          'bayes',
          'Derive Bayes’ rule by counting the routes to evidence',
          [
            {
              kind: 'paragraph',
              text: "Sometimes we know how often each group produces a signal but want to infer the group from the signal. Bayes’ rule reverses that direction. Substitute the multiplication rule into the definition of conditional probability to obtain the equation below. The factor P(A) is the prior probability, before the signal; P(B | A) is the signal's likelihood within A; P(A | B) is the posterior probability after observing it.",
            },
            {
              kind: 'equation',
              label: 'Bayes’ rule',
              lines: [
                {
                  left: 'P(A | B)',
                  right: {
                    numerator: 'P(B | A) · P(A)',
                    denominator: 'P(B)',
                  },
                },
              ],
              note: 'Prior P(A) × likelihood P(B | A), divided by the probability of the observed evidence B.',
            },
            {
              kind: 'paragraph',
              text: 'To find P(B), divide the population into A and not A. These groups do not overlap and cover every possibility. A signal can come from either group, so count both contributions as shown below. Adding is justified because no outcome belongs to both groups. This is the law of total probability for two groups. The denominator includes every route to the signal, not just the route we hope caused it.',
            },
            {
              kind: 'equation',
              label: 'Account for every route to the evidence',
              lines: [
                {
                  left: 'P(B)',
                  right: 'P(B | A)P(A) + P(B | not A)P(not A)',
                },
              ],
              note: 'The two groups do not overlap, so their contributions add.',
            },
            {
              kind: 'example',
              title: 'Example · Which route produced a late parcel?',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'As a separate example, 20% of parcels use route A and the rest route C. A late label appears on 50% of A parcels and 10% of C parcels. Among 100 representative parcels, that gives 10 late A parcels and 8 late C parcels. A late parcel therefore has probability 10/18 = 5/9 of coming from A. In symbols, (0.50 × 0.20) / [(0.50 × 0.20) + (0.10 × 0.80)]. The large route C contributes many signals despite its lower late rate. Both the signal rate and the original group size matter. The production-line problem uses the same reasoning, with “defective” as the group and “flagged” as the evidence.',
                },
                {
                  kind: 'table',
                  caption: 'Expected counts among 100 representative parcels',
                  headers: ['Route', 'All parcels', 'Late share', 'Late parcels'],
                  rows: [
                    ['A', '20', '50%', '10'],
                    ['C', '80', '10%', '8'],
                    ['Total', '100', '—', '18'],
                  ],
                },
                {
                  kind: 'equation',
                  label: 'Reverse the direction',
                  lines: [
                    {
                      left: 'P(route A | late)',
                      right: {
                        numerator: '10',
                        denominator: '18',
                      },
                    },
                  ],
                  note: 'The late parcels form the denominator; 10/18 = 5/9.',
                },
              ],
            },
          ],
          'Method',
        ),
        section(
          'population',
          'Make the base rate visible',
          [
            {
              kind: 'paragraph',
              text: 'The production-line check can flag an item for either of two reasons: it detects an actual defect, or it flags an item that is not defective. We are selecting from the combined flagged group. The fraction of defects in that group depends on the size of both contributions.',
            },
            {
              kind: 'paragraph',
              text: 'A test’s sensitivity describes its performance among defective items. The question asks about a different group: items that were flagged. To move between those groups, we need to know how common defects were before the check. That prior defect rate is one percent.',
            },
            {
              kind: 'paragraph',
              text: 'Imagine 10,000 representative items. The model gives 100 defective items and 9,900 nondefective ones in expected counts. The check flags 95 of the defective group. It also flags five percent of the much larger nondefective group, which is 495 items. These are expected counts used to express probabilities, not a claim that every real batch has exactly those totals.',
            },
            {
              kind: 'table',
              caption: 'Daily application: expected counts in 10,000 items',
              headers: ['Group', 'Total items', 'Flagged', 'Not flagged'],
              rows: [
                ['Defective', '100', '95', '5'],
                ['Nondefective', '9,900', '495', '9,405'],
                ['Total', '10,000', '590', '9,410'],
              ],
            },
          ],
          'Daily application',
        ),
        section(
          'reverse',
          'Divide by everyone who could produce the observation',
          [
            {
              kind: 'paragraph',
              text: 'There are 95 + 495 = 590 flagged items in this probability table. Of those, 95 are defective. Thus P(defective given flagged) = 95/590 = 19/118, approximately 0.1610, or 16.10 percent.',
            },
            {
              kind: 'equation',
              label: 'Among the flagged items',
              lines: [
                {
                  left: 'P(defective | flagged)',
                  right: {
                    numerator: '95',
                    denominator: '95 + 495',
                  },
                },
              ],
              note: '19/118 ≈ 16.10%. The denominator includes both causes of a flag.',
            },
            {
              kind: 'paragraph',
              text: 'In symbols, let D mean defective and F mean flagged. Bayes’ rule gives P(D given F) = P(F given D)P(D) / [P(F given D)P(D) + P(F given not D)P(not D)]. Substituting yields (0.95 × 0.01) / [(0.95 × 0.01) + (0.05 × 0.99)]. The denominator contains both routes to a flag, not just the route we hope to confirm.',
            },
            {
              kind: 'paragraph',
              text: 'The number 95 percent answers “How often is a defective item flagged?” It does not answer “How often is a flagged item defective?” Reversing the words around “given” changes which population supplies the denominator. Writing the denominator in plain language is often more useful than memorizing the formula.',
            },
          ],
          'Daily application',
        ),
        section(
          'lesson',
          'A useful signal need not be a final verdict',
          [
            {
              kind: 'paragraph',
              text: 'A flag increases the defect probability from one percent to about sixteen percent, so it is informative. At the same time, most flagged items are still nondefective because nondefective items were so numerous initially. Both statements can be true. Calling the check either perfect or useless would miss the role of the base rate.',
            },
            {
              kind: 'paragraph',
              text: 'For a second check, we could not simply square the false-positive rate unless its errors were appropriately independent of the first check’s errors given defect status. Two checks that both react to the same harmless surface mark may repeat the same mistake. The present problem asks about only one check and specifies all three rates needed for its calculation.',
            },
            {
              kind: 'paragraph',
              text: 'The same calculation can be checked without scaling to 10,000 items. Of the entire population, 0.01 × 0.95 = 0.0095 is both defective and flagged; 0.99 × 0.05 = 0.0495 is nondefective and flagged. Dividing 0.0095 by their sum, 0.0590, gives the same 19/118. Multiplying both contributions by any common population size leaves that fraction unchanged.',
            },
          ],
          'Check',
        ),
      ],
    },
  }),
];
